import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CalendarCheck,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  LayoutDashboard,
  Printer,
  RefreshCw,
  Wallet,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'

import RoleOnboarding from '../../components/RoleOnboarding'
import { ErrorState, LoadingState } from '../../components/PageState'
import { api } from '../../services/api'

interface SummaryCard { label: string; value: string | number; link: string }
interface QuickLink { label: string; href: string }
interface Attention { level: string; message: string; link: string }
// Indexed so recharts accepts these rows directly as chart data.
interface ChartPoint { [key: string]: unknown; label: string; value: number }
interface RoleCharts {
  enrollment_by_class?: ChartPoint[]
  attendance_trend?: ChartPoint[]
  admissions_by_status?: ChartPoint[]
  students_by_gender?: ChartPoint[]
  staff_by_department?: ChartPoint[]
}
interface FinanceSnapshot { outstanding_fees: number; collected_this_month: number; expenses_this_month: number }
interface TodayAttendance { marked: number; present: number; absent: number; rate: number }
interface ActivityItem { title: string; category: string; timestamp: string; link: string }
interface RoleSummary {
  role: string
  title: string
  subtitle: string
  generated_at: string
  cards: SummaryCard[]
  quick_links: QuickLink[]
  attention: Attention[]
  charts?: RoleCharts | null
  finance_snapshot?: FinanceSnapshot | null
  today_attendance?: TodayAttendance | null
  recent_activity?: ActivityItem[]
}

const PIE_COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777']

const currencyFormatter = new Intl.NumberFormat('en-UG', {
  style: 'currency',
  currency: 'UGX',
  maximumFractionDigits: 0,
})

function formatCurrency(value: number) {
  return currencyFormatter.format(value ?? 0)
}

function escapeCsvValue(value: unknown) {
  return `"${String(value ?? '')
    .replace(/\r?\n/g, ' ')
    .replace(/"/g, '""')}"`
}

function downloadBlob(filename: string, content: BlobPart, mimeType: string) {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function classifyAttentionLevel(level: string) {
  const normalized = level?.toLowerCase() ?? ''
  if (['high', 'critical', 'urgent'].includes(normalized)) return { label: 'Urgent', badgeClass: 'bg-red-100 text-red-700' }
  if (['medium', 'warning'].includes(normalized)) return { label: 'Attention', badgeClass: 'bg-amber-100 text-amber-700' }
  return { label: 'Info', badgeClass: 'bg-slate-100 text-slate-700' }
}

function buildAttentionRows(attention: Attention[]) {
  return attention.map((item) => ({
    task: item.message,
    priority: classifyAttentionLevel(item.level),
    action: item.link,
  }))
}

export default function OperationalRoleDashboard() {
  const query = useQuery({
    queryKey: ['operational-role-dashboard'],
    queryFn: async () => (await api.get('/dashboard/role-summary/')).data as RoleSummary,
    refetchInterval: 60_000,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)

  useEffect(() => {
    if (query.data) {
      const rows = buildAttentionRows(query.data.attention)
      const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
      setCurrentPage((page) => Math.min(page, totalPages))
    }
  }, [query.data, pageSize])

  if (query.isLoading) return <LoadingState label="Loading your live dashboard…" />
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="Your live summary could not be loaded. Check your connection and try again." retry={() => query.refetch()} />

  const data = query.data
  const rows = buildAttentionRows(data.attention)
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const paginatedRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const charts = data.charts
  const hasEnrollmentChart = Boolean(charts?.enrollment_by_class?.length)
  const hasAttendanceChart = Boolean(charts?.attendance_trend?.length)
  const hasAdmissionsChart = Boolean(charts?.admissions_by_status?.length)
  const hasGenderChart = Boolean(charts?.students_by_gender?.length)
  const hasStaffChart = Boolean(charts?.staff_by_department?.length)
  const hasCharts = hasEnrollmentChart || hasAttendanceChart || hasAdmissionsChart || hasGenderChart || hasStaffChart
  const finance = data.finance_snapshot
  const todayAttendance = data.today_attendance
  const recentActivity = data.recent_activity ?? []
  const hasHighlights = Boolean(finance || todayAttendance)

  const exportDashboard = (format: 'csv' | 'excel' | 'pdf') => {
    const headers = ['Item', 'Priority', 'Link']
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        [row.task, row.priority.label, row.action]
          .map((value) => escapeCsvValue(value))
          .join(','),
      ),
    ].join('\n')

    if (format === 'csv') {
      downloadBlob('follow-ups.csv', csvContent, 'text/csv;charset=utf-8;')
      return
    }

    if (format === 'pdf') {
      const printWindow = window.open('', '_blank', 'width=1200,height=900')
      if (!printWindow) return

      printWindow.document.write(`<!DOCTYPE html>
        <html>
          <head>
            <title>Follow-ups</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
              h1 { font-size: 22px; margin-bottom: 12px; }
              table { width: 100%; border-collapse: collapse; font-size: 12px; }
              th, td { border: 1px solid #d1d5db; padding: 8px 10px; text-align: left; }
              th { background: #f3f4f6; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <h1>Follow-ups</h1>
            <table>
              <thead>
                <tr>
                  <th>Item</th><th>Priority</th><th>Link</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (row) => `<tr><td>${row.task}</td><td>${row.priority.label}</td><td>${row.action}</td></tr>`,
                  )
                  .join('')}
              </tbody>
            </table>
          </body>
        </html>`)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => printWindow.print(), 500)
      return
    }

    const workbookXml = `<?xml version="1.0"?>
      <Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
        <Worksheet ss:Name="DashboardOverview">
          <Table>
            <Row>
              ${headers.map((header) => `<Cell><Data ss:Type="String">${header}</Data></Cell>`).join('')}
            </Row>
            ${rows
              .map(
                (row) => `<Row>
                  <Cell><Data ss:Type="String">${row.task}</Data></Cell>
                  <Cell><Data ss:Type="String">${row.priority.label}</Data></Cell>
                  <Cell><Data ss:Type="String">${row.action}</Data></Cell>
                </Row>`,
              )
              .join('')}
          </Table>
        </Worksheet>
      </Workbook>`

    downloadBlob('follow-ups.xls', workbookXml, 'application/vnd.ms-excel;charset=utf-8;')
  }

  return <div className="space-y-6">
    <div className="flex flex-wrap items-start justify-between gap-3"><div><h1 className="text-2xl font-bold text-gray-900">{data.title}</h1><p className="text-gray-600">{data.subtitle}</p></div><button onClick={() => query.refetch()} className="btn btn-outline"><RefreshCw className={`mr-2 h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />Refresh</button></div>
    <RoleOnboarding />
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data.cards.map(card => <Link key={card.label} to={card.link} className="card transition hover:border-primary-300 hover:shadow"><div className="card-content flex items-center justify-between"><div><div className="text-sm text-gray-500">{card.label}</div><div className="mt-1 text-2xl font-semibold text-gray-900">{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</div></div><LayoutDashboard className="h-7 w-7 text-primary-500" /></div></Link>)}</div>
    {hasHighlights && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {finance && <>
        <div className="card"><div className="card-content flex items-center justify-between">
          <div><div className="text-sm text-gray-500">Collected this month</div><div className="mt-1 text-xl font-semibold text-emerald-700">{formatCurrency(finance.collected_this_month)}</div></div>
          <Wallet className="h-7 w-7 text-emerald-500" />
        </div></div>
        <div className="card"><div className="card-content flex items-center justify-between">
          <div><div className="text-sm text-gray-500">Outstanding fees</div><div className="mt-1 text-xl font-semibold text-amber-700">{formatCurrency(finance.outstanding_fees)}</div></div>
          <Wallet className="h-7 w-7 text-amber-500" />
        </div></div>
      </>}
      {todayAttendance && <>
        <div className="card"><div className="card-content flex items-center justify-between">
          <div><div className="text-sm text-gray-500">Present today</div><div className="mt-1 text-xl font-semibold text-gray-900">{todayAttendance.present.toLocaleString()} <span className="text-sm font-normal text-gray-500">of {todayAttendance.marked.toLocaleString()}</span></div></div>
          <CalendarCheck className="h-7 w-7 text-primary-500" />
        </div></div>
        <div className="card"><div className="card-content flex items-center justify-between">
          <div><div className="text-sm text-gray-500">Today's attendance rate</div><div className="mt-1 text-xl font-semibold text-gray-900">{todayAttendance.marked ? `${todayAttendance.rate}%` : 'Not marked yet'}</div></div>
          <CalendarCheck className="h-7 w-7 text-primary-500" />
        </div></div>
      </>}
    </div>}
    {hasCharts && <div className="grid gap-5 lg:grid-cols-3">
      {hasEnrollmentChart && <div className="card lg:col-span-2"><div className="card-content">
        <h2 className="font-semibold text-gray-900">Enrollment by class</h2>
        <p className="mt-1 text-sm text-gray-500">Active students per class this year.</p>
        <div className="mt-4 h-64"><ResponsiveContainer width="100%" height={256}>
          <BarChart data={charts?.enrollment_by_class}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} angle={-30} textAnchor="end" height={50} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <RechartsTooltip />
            <Bar dataKey="value" name="Students" fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div>
      </div></div>}
      {hasAdmissionsChart && <div className="card"><div className="card-content">
        <h2 className="font-semibold text-gray-900">Admissions pipeline</h2>
        <p className="mt-1 text-sm text-gray-500">Applications by current status.</p>
        <div className="mt-4 h-64"><ResponsiveContainer width="100%" height={256}>
          <PieChart>
            <Pie data={charts?.admissions_by_status} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
              {charts?.admissions_by_status?.map((entry, index) => <Cell key={entry.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
            </Pie>
            <RechartsTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer></div>
      </div></div>}
      {hasGenderChart && <div className="card"><div className="card-content">
        <h2 className="font-semibold text-gray-900">Students by gender</h2>
        <p className="mt-1 text-sm text-gray-500">Active enrollment split.</p>
        <div className="mt-4 h-64"><ResponsiveContainer width="100%" height={256}>
          <PieChart>
            <Pie data={charts?.students_by_gender} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
              {charts?.students_by_gender?.map((entry, index) => <Cell key={entry.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
            </Pie>
            <RechartsTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer></div>
      </div></div>}
      {hasStaffChart && <div className="card lg:col-span-2"><div className="card-content">
        <h2 className="font-semibold text-gray-900">Staff by department</h2>
        <p className="mt-1 text-sm text-gray-500">Active staff headcount per department.</p>
        <div className="mt-4 h-64"><ResponsiveContainer width="100%" height={256}>
          <BarChart data={charts?.staff_by_department} layout="vertical" margin={{ left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 12 }} />
            <RechartsTooltip />
            <Bar dataKey="value" name="Staff" fill="#7c3aed" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer></div>
      </div></div>}
      {hasAttendanceChart && <div className="card lg:col-span-3"><div className="card-content">
        <h2 className="font-semibold text-gray-900">Attendance rate (last 14 days)</h2>
        <p className="mt-1 text-sm text-gray-500">Share of marked students recorded present or late each day.</p>
        <div className="mt-4 h-64"><ResponsiveContainer width="100%" height={256}>
          <LineChart data={charts?.attendance_trend}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 12 }} />
            <RechartsTooltip formatter={(value: number) => [`${value}%`, 'Attendance']} />
            <Line type="monotone" dataKey="value" name="Attendance" stroke="#059669" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer></div>
      </div></div>}
    </div>}
    {data.attention.length > 0 && <div className="rounded border border-amber-200 bg-amber-50"><div className="border-b border-amber-200 px-4 py-3 font-semibold text-amber-900">Needs attention</div>{data.attention.map((item, index) => <Link key={`${item.message}-${index}`} to={item.link} className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-amber-800 hover:bg-amber-100"><span><AlertTriangle className="mr-2 inline h-4 w-4" />{item.message}</span><ArrowRight className="h-4 w-4" /></Link>)}</div>}
    {recentActivity.length > 0 && <div className="card"><div className="card-content">
      <h2 className="font-semibold text-gray-900">Recent activity</h2>
      <p className="mt-1 text-sm text-gray-500">Latest published announcements and notices.</p>
      <ul className="mt-4 divide-y divide-gray-100">
        {recentActivity.map((item, index) => <li key={`${item.title}-${index}`}>
          <Link to={item.link} className="flex items-center justify-between gap-3 py-3 text-sm hover:text-primary-700">
            <span className="flex items-center gap-2 text-gray-700"><Bell className="h-4 w-4 text-gray-400" />{item.title}<span className="text-xs text-gray-400">· {item.category}</span></span>
            <span className="whitespace-nowrap text-xs text-gray-400">{new Date(item.timestamp).toLocaleDateString()}</span>
          </Link>
        </li>)}
      </ul>
    </div></div>}
    <div className="card"><div className="card-content">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-semibold text-gray-900">Priority follow-ups</h2><p className="mt-1 text-sm text-gray-500">Every open item that needs your action, in one exportable list.</p></div>
        {rows.length > 0 && <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => exportDashboard('csv')} className="btn btn-outline inline-flex items-center gap-2"><FileText className="h-4 w-4" />CSV</button>
          <button type="button" onClick={() => exportDashboard('excel')} className="btn btn-outline inline-flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" />Excel</button>
          <button type="button" onClick={() => exportDashboard('pdf')} className="btn btn-outline inline-flex items-center gap-2"><Printer className="h-4 w-4" />PDF</button>
        </div>}
      </div>
      {rows.length === 0
        ? <div className="mt-6 flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-200 py-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="text-sm font-medium text-gray-700">You're all caught up</p>
            <p className="text-sm text-gray-500">No follow-ups need your attention right now.</p>
          </div>
        : <>
            <div className="mt-4 overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-left">
              <thead><tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600"><th className="px-3 py-3 font-semibold">Item</th><th className="px-3 py-3 font-semibold">Priority</th><th className="px-3 py-3 font-semibold text-right">Link</th></tr></thead>
              <tbody className="divide-y divide-gray-100 bg-white">{paginatedRows.map((row, index) => <tr key={`${row.task}-${index}`} className="hover:bg-gray-50">
                <td className="px-3 py-3 text-sm font-medium text-gray-800">{row.task}</td>
                <td className="px-3 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${row.priority.badgeClass}`}>{row.priority.label}</span></td>
                <td className="px-3 py-3 text-right text-sm"><Link to={row.action} className="font-medium text-primary-600 hover:text-primary-700">Open</Link></td>
              </tr>)}</tbody>
            </table></div>
            {totalPages > 1 && <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">Showing {Math.min((currentPage - 1) * pageSize + 1, rows.length)}-{Math.min(currentPage * pageSize, rows.length)} of {rows.length} items</p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))} disabled={currentPage === 1} className="btn btn-outline px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => <button key={page} type="button" onClick={() => setCurrentPage(page)} className={`h-9 min-w-9 rounded-md border px-2 text-sm ${currentPage === page ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>{page}</button>)}
                <button type="button" onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))} disabled={currentPage === totalPages} className="btn btn-outline px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
              </div>
            </div>}
          </>}
    </div></div>
    <div className="card"><div className="card-content">
      <h2 className="font-semibold text-gray-900">Your common tasks</h2>
      <p className="mt-1 text-sm text-gray-500">Open a workspace to view records and complete your daily work.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.quick_links.map(link => <Link key={link.href} to={link.href} className="group flex items-center gap-3 rounded-lg border border-gray-200 p-3 text-sm font-medium text-gray-700 transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-sm">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 group-hover:bg-primary-100"><LayoutDashboard className="h-4 w-4" /></span>
          <span className="flex-1">{link.label}</span>
          <ArrowRight className="h-4 w-4 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
        </Link>)}
      </div>
    </div></div>
    <div className="text-right text-xs text-gray-400">Updated {new Date(data.generated_at).toLocaleString()}</div>
  </div>
}
